/**
 * AdjustmentQuickButton — Botón "Ajuste C.C." (v3.47.14).
 *
 * Ajuste PURO de cuenta corriente: NO mueve caja/banco. Impacta sólo la cta.cte.
 * del tercero (residente/proveedor) y una cuenta de resultado, vía el Concepto de
 * Ingreso/Egreso elegido (que ya está mapeado a su cuenta contable).
 *
 * Props:
 *   targetType: 'supplier' | 'resident'
 *   targetId:   string
 *   targetLabel: string (para mostrar en el título del diálogo)
 *   onDone:     callback tras guardar OK (para refrescar la cta.cte.)
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { SlidersHorizontal } from 'lucide-react';
import { useTreasurySubmit, idempotencyHeader } from '../lib/useTreasurySubmit';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// (target_type, direction) → tipo de concepto contable a usar.
const deriveConceptType = (targetType, direction) => {
  if ((targetType === 'resident' && direction === 'increase') ||
      (targetType === 'supplier' && direction === 'decrease')) return 'income';
  return 'expense';
};

const DIRECTION_LABELS = {
  resident: {
    increase: 'Aumentar la deuda del residente (cargo / interés)',
    decrease: 'Disminuir la deuda del residente (bonificación / condonación)',
  },
  supplier: {
    increase: 'Aumentar lo que le debo al proveedor (cargo / interés)',
    decrease: 'Disminuir lo que le debo al proveedor (descuento / nota de crédito)',
  },
};

const emptyForm = () => ({
  date: new Date().toISOString().split('T')[0],
  direction: '',
  concept_id: '',
  amount: '',
  description: '',
});

export function AdjustmentQuickButton({ targetType, targetId, targetLabel, onDone,
                                        size = 'sm', variant = 'outline', className = '' }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [incomeConcepts, setIncomeConcepts] = useState([]);
  const [expenseConcepts, setExpenseConcepts] = useState([]);
  const submit = useTreasurySubmit();

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const [ic, ec] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/treasury/income-concepts/active`, { withCredentials: true }),
          axios.get(`${BACKEND_URL}/api/treasury/expense-concepts/active`, { withCredentials: true }),
        ]);
        setIncomeConcepts(ic.data?.items || ic.data || []);
        setExpenseConcepts(ec.data?.items || ec.data || []);
      } catch (e) {
        toast.error('No se pudieron cargar los conceptos');
      }
    })();
  }, [open]);

  const openDialog = () => { setForm(emptyForm()); setOpen(true); };

  const conceptType = form.direction ? deriveConceptType(targetType, form.direction) : null;
  const conceptList = conceptType === 'income' ? incomeConcepts
    : conceptType === 'expense' ? expenseConcepts : [];

  const setDirection = (d) => setForm(f => ({ ...f, direction: d, concept_id: '' }));

  const save = async () => {
    return submit.run(async (idemKey) => {
      if (!form.direction) { toast.error('Elegí el tipo de ajuste'); return; }
      if (!form.concept_id) { toast.error('Elegí un concepto'); return; }
      const amount = parseFloat(form.amount);
      if (!amount || amount <= 0) { toast.error('Ingresá un monto mayor a cero'); return; }
      try {
        await axios.post(`${BACKEND_URL}/api/treasury/cc-adjustments`, {
          date: form.date,
          target_type: targetType,
          target_id: targetId,
          direction: form.direction,
          concept_id: form.concept_id,
          amount,
          description: form.description || '',
        }, { withCredentials: true, headers: idempotencyHeader(idemKey) });
        toast.success('Ajuste de cuenta corriente registrado');
        setOpen(false);
        onDone?.();
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Error al guardar el ajuste');
      }
    });
  };

  return (
    <>
      <Button size={size} variant={variant} onClick={openDialog} className={className}
              data-testid={`adjust-cc-btn-${targetType}`}>
        <SlidersHorizontal className="w-4 h-4 mr-1" />Ajuste C.C.
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="cc-adjust-dialog">
          <DialogHeader>
            <DialogTitle>Ajuste de cuenta corriente</DialogTitle>
            <DialogDescription>
              Corrige el saldo de <b>{targetLabel}</b> sin mover caja/banco. Impacta la
              cuenta corriente y la cuenta contable del concepto elegido.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div>
              <Label className="text-xs">Fecha</Label>
              <Input type="date" value={form.date}
                     onChange={e => setForm(f => ({ ...f, date: e.target.value }))}
                     className="h-9" data-testid="cc-adjust-date" />
            </div>

            <div>
              <Label className="text-xs">Tipo de ajuste</Label>
              <Select value={form.direction} onValueChange={setDirection}>
                <SelectTrigger className="h-9" data-testid="cc-adjust-direction">
                  <SelectValue placeholder="Elegí qué hace el ajuste…" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="increase" data-testid="cc-adjust-direction-increase">
                    {DIRECTION_LABELS[targetType].increase}
                  </SelectItem>
                  <SelectItem value="decrease" data-testid="cc-adjust-direction-decrease">
                    {DIRECTION_LABELS[targetType].decrease}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs">
                Concepto {conceptType ? `(${conceptType === 'income' ? 'ingreso' : 'egreso'})` : ''}
              </Label>
              <Select value={form.concept_id} disabled={!form.direction}
                      onValueChange={v => setForm(f => ({ ...f, concept_id: v }))}>
                <SelectTrigger className="h-9" data-testid="cc-adjust-concept">
                  <SelectValue placeholder={form.direction ? 'Elegí el concepto contable…' : 'Primero elegí el tipo de ajuste'} />
                </SelectTrigger>
                <SelectContent>
                  {conceptList.map(c => (
                    <SelectItem key={c.concept_id} value={c.concept_id}
                                data-testid={`cc-adjust-concept-${c.concept_id}`}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs">Monto</Label>
              <Input type="number" min="0" step="0.01" value={form.amount}
                     onChange={e => setForm(f => ({ ...f, amount: e.target.value }))}
                     placeholder="0,00" className="h-9" data-testid="cc-adjust-amount" />
            </div>

            <div>
              <Label className="text-xs">Descripción</Label>
              <Textarea rows={2} value={form.description}
                        onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                        placeholder="Motivo del ajuste…" data-testid="cc-adjust-description" />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={submit.submitting}
                    data-testid="cc-adjust-cancel">Cancelar</Button>
            <Button onClick={save} disabled={submit.submitting} data-testid="cc-adjust-save">
              {submit.submitting ? 'Guardando…' : 'Registrar ajuste'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
